import React from 'react'
import {
    DeleteOutlined,
    ReloadOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'

import { StyledButton } from '../../Button'
import { MEDICATION_STATUS, PHARMACY_STATUS, PRESCRIPTION_ACTIONS } from '../../../constants'

const PrescriptionItemContainer = ({ children, defaults, hasError = false }) => (
    <div style={{
        width: '100%',
        padding: '20px',
        marginBottom: '20px',
        background: hasError ? 'rgba(255, 77, 0, 0.13)' : defaults.background,
        color: hasError ? 'rgb(255, 0, 0)' : defaults.color,
        borderRadius: '5px',
        display: 'flex',
        flexDirection: 'column',
    }}>
        {children}
    </div>
)

export const PrescriptionDisplay = ({
    prescription,
    isAssignedProvider = false,
    setPrescriptionAndAction,
    isAdmin = false,
    getDispenseUnitById,
}) => {
    const cancelled = [
        MEDICATION_STATUS.CANCEL_PENDING,
        MEDICATION_STATUS.CANCEL,
        MEDICATION_STATUS.CANCELED,
    ].includes(prescription.medicationStatus)

    const prescriptionBackground = cancelled ? 'rgba(255, 0, 0, .2)' : 'rgb(221, 221, 221)'

    const canBeDeleted = [
        PHARMACY_STATUS.READY_TO_SIGN,
        PHARMACY_STATUS.ENTERED,
        PHARMACY_STATUS.EPCS_ERROR,
        PHARMACY_STATUS.EPCS_SIGNED
    ].includes(prescription.pharmacyStatus)

    const canBeResent = ![
        MEDICATION_STATUS.DISCONTINUED,
        MEDICATION_STATUS.INACTIVE,
        MEDICATION_STATUS.CANCEL_REQUESTED,
        MEDICATION_STATUS.CANCEL_PENDING,
        MEDICATION_STATUS.CANCEL_DENIED,
    ].includes(prescription.medicationStatus) &&
        prescription.pharmacyStatus !== PHARMACY_STATUS.PHARMACY_VERIFIED

    const handleActionClick = (action) => {

        if (setPrescriptionAndAction) {
            setPrescriptionAndAction({ prescription, action })
        } else {
            console.error('setPrescriptionAndAction is not available!')
        }
    }

    const hasError = prescription.pharmacyStatus?.toLowerCase().includes('error')

    return (
        <PrescriptionItemContainer
            defaults={{
                background: prescriptionBackground,
                color:'#000'
            }}
            hasError={hasError}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong>{prescription.name}</strong>
                {(!hasError && (isAssignedProvider || isAdmin)) && (
                    <div style={{
                        textTransform: 'capitalize',
                        display: 'flex',
                        gap: '5px'
                    }}>
                        {setPrescriptionAndAction && canBeResent && (
                            <StyledButton
                                size='small'
                                Icon={ReloadOutlined}
                                tooltip="Resend Prescription"
                                color='green'
                                onClick={() => handleActionClick(PRESCRIPTION_ACTIONS.RESEND)}
                            />
                        )}
                        {setPrescriptionAndAction && (
                            <StyledButton
                                size='small'
                                Icon={DeleteOutlined}
                                tooltip={
                                    canBeDeleted ?
                                        "Delete Prescription" :
                                        "Request Cancellation of Prescription?"
                                }
                                color='red'
                                onClick={() => handleActionClick(canBeDeleted ? PRESCRIPTION_ACTIONS.DELETE : PRESCRIPTION_ACTIONS.CANCEL)}
                            />
                        )}
                    </div>
                )}
            </div>

            {prescription.signedAt && (
                <div style={{ fontSize: '12px', marginBottom: '5px' }}>
                    Written Date: {dayjs(prescription.signedAt).tz(dayjs.tz.guess()).format('M/DD/YYYY h:mm A')}
                </div>
            )}

            <div style={{ display: 'flex', gap: '20px', marginBottom: '5px' }}>
                <span>
                    {prescription.quantity} {`${getDispenseUnitById(prescription.dispenseUnitId)?.Name ?? 'unit'}(s)`}
                </span>
                <span>Refills: {prescription.refills}</span>
                <span>Supply: {prescription.daysSupply} days</span>
            </div>

            {
                hasError ? (
                    <strong>
                        <ExclamationCircleOutlined />
                        {` Error: ${prescription.pharmacyStatus}`}
                    </strong>
                ) : (
                    <>
                        {prescription.directions && (
                            <div><strong>Directions:</strong> {prescription.directions}</div>
                        )}

                        {prescription.pharmacy && (
                            <div><strong>Pharmacy:</strong> {prescription.pharmacy.name || "ERROR"}</div>
                        )}

                        {prescription.pharmacyId && (
                            <div><strong>Pharmacy ID:</strong> {prescription.pharmacyId}</div>
                        )}

                        {prescription.pharmacyNotes && (
                            <div><strong>Pharmacy Notes:</strong> {prescription.pharmacyNotes}</div>
                        )}

                        {prescription.pharmacyStatus && (
                            <div><strong>Pharmacy Status:</strong> {prescription.pharmacyStatus}</div>
                        )}

                        {prescription.medicationStatus && (
                            <div>
                                <strong>Medication Status: </strong>
                                <span style={{
                                    color: cancelled ? 'red' : 'black',
                                    fontWeight: cancelled ? 'bold' : 'normal'
                                }}>
                                    {prescription.medicationStatus}
                                </span>
                            </div>
                        )}
                    </>
                )
            }
        </PrescriptionItemContainer>
    )
}
